import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { SOCKET_ID_VERSION_CONFIG_KEY } from "../../utils/dynamicSockets/dynamicSocketId";

const ColumnTypeSchema = z.enum([
  "string",
  "integer",
  "float",
  "numeric",
  "boolean",
  "date",
]);

const ColumnSnapshotSchema = z.object({
  name: z.string().min(1),
  type: ColumnTypeSchema,
});

/**
 * Payload keys the handler always emits alongside the per-column values.
 *
 * A column named `frameNumber` would land on the same key, and since socket
 * ids double as payload keys the column's value would be overwritten by the
 * frame number (or vice versa) with nothing to indicate it happened. Reserved
 * on version 1; under `__socketIdVersion: 2` the marker makes the two disjoint
 * and such a column is usable.
 */
export const LOOKUP_ROW_RESERVED_OUTPUT_SOCKET_IDS = [
  "frameNumber",
  "versionNumber",
] as const;

export const statefulTableLookupRowStep = defineStepConfig({
  type: "stateful_table_lookup_row",
  name: "Stateful Table Lookup Row",
  description:
    "Look up a single row in a stateful table by its key column. Outputs one typed socket per column from the table's current schema.",
  category: "Data Access",

  inputSchema: z.object({
    key: z
      .string()
      .min(1)
      .describe("The value to look up against the table's key column."),
  }),

  outputSchema: z
    .object({
      frameNumber: z.number().int().min(1),
      versionNumber: z.number().int().min(1),
    })
    // The handler fans the matched row into one output per column (the
    // per-column dynamic sockets). Output validation `.parse()`s the return
    // value, and without catchall a plain z.object strips those column keys —
    // leaving every per-column socket empty at runtime.
    .catchall(z.unknown()),

  configSchema: z.object({
    tableDefinitionId: z
      .string()
      .uuid()
      .describe("ID of the stateful table to read from"),
    columns: z
      .array(ColumnSnapshotSchema)
      .min(1)
      .describe(
        "Snapshot of the table's column schema at design time. Drives the per-column output sockets.",
      ),
    // Declared so z.object() does not strip the stamp on parse. `executeStep`
    // parses the persisted config through this schema before handing it to the
    // handler, and the handler derives its payload keys from the version: drop
    // the stamp and it writes bare ids for sockets the canvas marked
    // `::dynamic`, leaving every per-column output reading undefined.
    [SOCKET_ID_VERSION_CONFIG_KEY]: z
      .number()
      .int()
      .optional()
      .describe(
        "Socket-id scheme this step was created under. Absent means the legacy scheme, where a column name IS its output socket id.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "2 minutes",
    retry: { maximumAttempts: 2 },
  },
});
